/**
 * Notifications Store - AccountsGenie
 * Zustand store for managing notification data and state
 */

import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import {
  Notification,
  NotificationStats,
  NotificationFilters,
  NotificationPreferences,
  NotificationStore
} from '../types/notifications';
import { notificationsService } from '../services/notifications';

const initialStats: NotificationStats = {
  total: 0,
  unread: 0,
  byType: {
    info: 0,
    success: 0,
    warning: 0,
    error: 0,
    system: 0,
    payment: 0,
    report: 0
  },
  byPriority: {
    low: 0,
    medium: 0,
    high: 0,
    critical: 0
  }
};

const defaultPreferences: NotificationPreferences = {
  enableSound: true,
  enableBrowserNotifications: true,
  enableEmailNotifications: false,
  types: {
    info: true,
    success: true,
    warning: true,
    error: true,
    system: true,
    payment: true,
    report: true
  },
  quietHours: {
    enabled: false,
    start: '22:00',
    end: '08:00'
  }
};

export const useNotificationsStore = create<NotificationStore>()(
  devtools(
    (set, get) => ({
      notifications: [],
      stats: initialStats,
      isLoading: false,
      error: null,
      preferences: defaultPreferences,

      /**
       * Fetch notifications with optional filtering
       */
      fetchNotifications: async (filters?: NotificationFilters, cursor?: string) => {
        set({ isLoading: true, error: null });
        
        try {
          const response = await notificationsService.fetchNotifications(filters, cursor);
          
          set({
            notifications: cursor ? [...get().notifications, ...response.notifications] : response.notifications,
            stats: response.stats,
            isLoading: false
          });
        } catch (error) {
          console.error('Failed to fetch notifications:', error);
          set({
            error: error instanceof Error ? error.message : 'Failed to fetch notifications',
            isLoading: false
          });
        }
      },

      /**
       * Mark notification as read
       */
      markAsRead: async (id: string) => {
        try {
          await notificationsService.markAsRead(id);
          
          const notifications = get().notifications.map(notification =>
            notification.id === id
              ? { ...notification, isRead: true, readAt: new Date() }
              : notification
          );
          
          const unreadCount = notifications.filter(n => !n.isRead).length;
          
          set({
            notifications,
            stats: { ...get().stats, unread: unreadCount }
          });
        } catch (error) {
          console.error('Failed to mark notification as read:', error);
          set({
            error: error instanceof Error ? error.message : 'Failed to mark notification as read'
          });
        }
      },

      /**
       * Mark notification as unread
       */
      markAsUnread: async (id: string) => {
        try {
          await notificationsService.markAsUnread(id);
          
          const notifications = get().notifications.map(notification =>
            notification.id === id
              ? { ...notification, isRead: false, readAt: undefined }
              : notification
          );
          
          const unreadCount = notifications.filter(n => !n.isRead).length;
          
          set({
            notifications,
            stats: { ...get().stats, unread: unreadCount }
          });
        } catch (error) {
          console.error('Failed to mark notification as unread:', error);
          set({
            error: error instanceof Error ? error.message : 'Failed to mark notification as unread'
          });
        }
      },

      /**
       * Mark all notifications as read
       */
      markAllAsRead: async () => {
        try {
          await notificationsService.markAllAsRead();
          
          const now = new Date();
          const notifications = get().notifications.map(notification => ({
            ...notification,
            isRead: true,
            readAt: notification.readAt || now
          }));
          
          set({
            notifications,
            stats: { ...get().stats, unread: 0 }
          });
        } catch (error) {
          console.error('Failed to mark all notifications as read:', error);
          set({
            error: error instanceof Error ? error.message : 'Failed to mark all notifications as read'
          });
        }
      },

      /**
       * Delete notification
       */
      deleteNotification: async (id: string) => {
        try {
          await notificationsService.deleteNotification(id);
          
          const notifications = get().notifications.filter(n => n.id !== id);
          const unreadCount = notifications.filter(n => !n.isRead).length;
          
          set({
            notifications,
            stats: { 
              ...get().stats, 
              total: notifications.length,
              unread: unreadCount
            }
          });
        } catch (error) {
          console.error('Failed to delete notification:', error);
          set({
            error: error instanceof Error ? error.message : 'Failed to delete notification'
          });
        }
      },

      /**
       * Delete all read notifications
       */
      deleteAllRead: async () => {
        try {
          await notificationsService.deleteAllRead();
          
          const notifications = get().notifications.filter(n => !n.isRead);
          
          set({
            notifications,
            stats: { 
              ...get().stats, 
              total: notifications.length
            }
          });
        } catch (error) {
          console.error('Failed to delete read notifications:', error);
          set({
            error: error instanceof Error ? error.message : 'Failed to delete read notifications'
          });
        }
      },

      /**
       * Update notification preferences
       */
      updatePreferences: async (newPreferences: Partial<NotificationPreferences>) => {
        try {
          await notificationsService.updatePreferences(newPreferences);
          
          set({
            preferences: { ...get().preferences, ...newPreferences }
          });
        } catch (error) {
          console.error('Failed to update preferences:', error);
          set({
            error: error instanceof Error ? error.message : 'Failed to update preferences'
          });
        }
      },

      /**
       * Add new notification (for real-time updates)
       */
      addNotification: (notification: Notification) => {
        const notifications = [notification, ...get().notifications];
        const unreadCount = notifications.filter(n => !n.isRead).length;
        
        set({
          notifications,
          stats: { 
            ...get().stats, 
            total: notifications.length,
            unread: unreadCount
          }
        });

        // Show browser notification if enabled
        const { preferences } = get();
        if (preferences.enableBrowserNotifications && preferences.types[notification.type]) {
          notificationsService.showBrowserNotification(notification);
        }
      },

      /**
       * Update existing notification
       */
      updateNotification: (id: string, updates: Partial<Notification>) => {
        const notifications = get().notifications.map(notification =>
          notification.id === id
            ? { ...notification, ...updates }
            : notification
        );
        
        const unreadCount = notifications.filter(n => !n.isRead).length;
        
        set({
          notifications,
          stats: { ...get().stats, unread: unreadCount }
        });
      },

      /**
       * Get notification by ID
       */
      getNotificationById: (id: string) => {
        return get().notifications.find(notification => notification.id === id);
      },

      /**
       * Get unread count
       */
      getUnreadCount: () => {
        return get().notifications.filter(n => !n.isRead).length;
      },

      /**
       * Get notifications by type
       */
      getNotificationsByType: (type) => {
        return get().notifications.filter(n => n.type === type);
      }
    }),
    {
      name: 'notifications-store',
      version: 1
    }
  )
);

// Initialize store with preferences
export const initializeNotificationsStore = async () => {
  try {
    const preferences = await notificationsService.getPreferences();
    useNotificationsStore.setState({ preferences });
    
    // Fetch initial notifications
    await useNotificationsStore.getState().fetchNotifications();
  } catch (error) {
    console.error('Failed to initialize notifications store:', error);
  }
};